import { hasPermissions } from '@kontentino/react-permissions-gate';
import routes from 'constants/routes';
import { UserPermission } from 'constants/userPermission';

export interface RouteAccessPolicy {
  path: string;
  permissions: UserPermission[];
}

/**
 * Required permissions per top-level page (any-of semantics, matching
 * `hasPermissions`). Mirrors the navigation `permissionsScope`. Declared in
 * landing priority order — the redirect target resolves to the first entry the
 * user is permitted to open. Routes not listed here are not enforced.
 */
export const ROUTE_ACCESS_POLICIES: RouteAccessPolicy[] = [
  {
    path: routes.DASHBOARD,
    permissions: [
      UserPermission.ACCESS_DASHBOARD,
      UserPermission.ACCESS_MY_TASKS,
    ],
  },
  { path: routes.POSTS, permissions: [UserPermission.ACCESS_CALENDAR] },
  {
    path: routes.POSTS_APPROVALS,
    permissions: [
      UserPermission.POST_COMMUNICATION_CLIENT_SHOW,
      UserPermission.POST_COMMUNICATION_INTERNAL_SHOW,
    ],
  },
  { path: routes.ANALYTICS, permissions: [UserPermission.ACCESS_ANALYTICS] },
  { path: routes.INSIGHTS, permissions: [UserPermission.ACCESS_ANALYTICS] },
  { path: routes.INSIGHTS_LITE, permissions: [UserPermission.ACCESS_CALENDAR] },
  { path: routes.REPORTS, permissions: [UserPermission.ACCESS_ANALYTICS] },
  { path: routes.BRAND_HUB, permissions: [UserPermission.POST_CREATE] },
  { path: routes.BIO, permissions: [UserPermission.POST_CREATE] },
  {
    path: routes.MEDIA_LIBRARY,
    permissions: [UserPermission.ACCESS_MEDIA_LIBRARY],
  },
  { path: routes.SETTINGS.USERS, permissions: [UserPermission.ACCESS_USERS] },
];

const POLICIES_BY_SPECIFICITY = [...ROUTE_ACCESS_POLICIES].sort(
  (first, second) => second.path.length - first.path.length,
);

const matchesPath = (pathname: string, path: string): boolean =>
  pathname === path || pathname.startsWith(`${path}/`);

export const resolveRouteAccessPolicy = (
  pathname: string,
): RouteAccessPolicy | undefined =>
  POLICIES_BY_SPECIFICITY.find((policy) => matchesPath(pathname, policy.path));

export const resolveRedirectPath = (permissionsKeys: string[]): string => {
  const firstPermitted = ROUTE_ACCESS_POLICIES.find((policy) =>
    hasPermissions(permissionsKeys, policy.permissions),
  );
  return firstPermitted?.path ?? routes.POSTS;
};

export interface RouteAccessResult {
  allowed: boolean;
  redirectTo?: string;
}

/**
 * Decides whether a pathname may render for the given permissions. Unmapped
 * routes always render. On denial it returns a loop-proof redirect target: the
 * first permitted page, unless that resolves to the current path (degenerate
 * case), in which case it renders to avoid a redirect loop.
 */
export const evaluateRouteAccess = (
  pathname: string,
  permissionsKeys: string[],
): RouteAccessResult => {
  const policy = resolveRouteAccessPolicy(pathname);
  if (!policy) return { allowed: true };
  if (hasPermissions(permissionsKeys, policy.permissions)) {
    return { allowed: true };
  }

  const redirectTo = resolveRedirectPath(permissionsKeys);
  if (matchesPath(pathname, redirectTo)) return { allowed: true };
  return { allowed: false, redirectTo };
};
